/**
 * Legal Operations Intelligence — Premium Command Center v2
 * Practice Score · Priority Actions · Sparkline KPIs ·
 * Collection Funnel · Revenue Intelligence · Matter Table
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  TrendUp, TrendDown, Warning, WarningCircle,
  CheckCircle, Clock, Scales, Gavel, ArrowRight,
  DownloadSimple, CircleNotch,
  ArrowUpRight, Calendar, ChartBar, Target,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth/store';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useChambersMode } from '@/lib/useChambersMode';
import {
  fetchFinancialPerformance, fetchOutstandingFees,
  fetchOperationalAlerts, fetchMatterHealth, fetchHearingWorkloadTrend,
  downloadCSV,
} from '@/lib/reportsApi';
import { fetchDashboardMetrics } from '@/lib/meApi';
import { isOpenMatterStatus } from '@/lib/matterStatus';
import { BackButton } from '@/components/shared/BackButton';

// ─── Helpers ──────────────────────────────────────────────────

const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const fmt  = (n: number) => GBP.format(n);
const fmtK = (n: number) =>
  n >= 1_000_000 ? `£${(n / 1_000_000).toFixed(1)}m`
  : n >= 1_000   ? `£${(n / 1_000).toFixed(0)}k`
  : GBP.format(n);

type RangeMode = 'month' | 'quarter' | 'half' | 'year';

function dateRange(mode: RangeMode) {
  const to = new Date();
  const from = new Date(to);
  if (mode === 'month')        from.setMonth(from.getMonth() - 1);
  else if (mode === 'quarter') from.setMonth(from.getMonth() - 3);
  else if (mode === 'half')    from.setMonth(from.getMonth() - 6);
  else                         from.setFullYear(from.getFullYear() - 1);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function monthLabel(iso: string) {
  try {
    const [yr, mo] = iso.split('-');
    return new Date(Number(yr), Number(mo) - 1, 1)
      .toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  } catch { return iso; }
}

// ─── Practice score ────────────────────────────────────────────

function computePracticeScore(p: {
  collectionRate: number;
  stalledCount: number;
  atRiskCount: number;
  activeCount: number;
  criticalAlerts: number;
}): number {
  // Collection (45%): full at ≥90%, zero at <30%
  const collScore = Math.min(1, Math.max(0, (p.collectionRate - 30) / 60)) * 45;
  // Matter health (35%): penalise stalled + at-risk (at-risk = 2×)
  const flagged = p.stalledCount + p.atRiskCount * 2;
  const healthScore = p.activeCount > 0
    ? Math.max(0, 1 - flagged / p.activeCount) * 35
    : 35;
  // Alert component (20%)
  const alertScore = Math.max(0, 20 - p.criticalAlerts * 8);
  return Math.round(collScore + healthScore + alertScore);
}

// ─── Derived intelligence ─────────────────────────────────────

interface Insight {
  id: string;
  level: 'critical' | 'warning' | 'positive' | 'info';
  icon: React.ReactNode;
  title: string;
  body: string;
}

function deriveInsights(p: {
  monthly: Array<{ month: string; billed: number; collected: number }>;
  outstanding: Array<{ d90: number; total: number }>;
  health: Array<{ healthFlag: string }>;
  upcomingHearings: Array<{ date: string }>;
  activeMatters: Array<{ status: string }>;
  collectionRate: number;
  totalBilled: number;
  isSolo: boolean;
}): Insight[] {
  const out: Insight[] = [];

  if (p.monthly.length >= 2) {
    const last = p.monthly[p.monthly.length - 1]!;
    const prev = p.monthly[p.monthly.length - 2]!;
    const diff = prev.billed > 0 ? ((last.billed - prev.billed) / prev.billed) * 100 : 0;
    if (diff > 15) out.push({ id: 'rev-up', level: 'positive', icon: <TrendUp size={13} weight="bold" />,
      title: `Revenue up ${Math.round(diff)}% this month`,
      body: `${monthLabel(last.month)} billing of ${fmtK(last.billed)} is the strongest recent period.` });
    else if (diff < -20) out.push({ id: 'rev-down', level: 'warning', icon: <TrendDown size={13} weight="bold" />,
      title: `Billing dropped ${Math.abs(Math.round(diff))}% this month`,
      body: `${monthLabel(last.month)} shows ${fmtK(last.billed)} billed. Review the fee note pipeline.` });
  }

  const total90 = p.outstanding.reduce((s, r) => s + r.d90, 0);
  const totalOs = p.outstanding.reduce((s, r) => s + r.total, 0);
  if (total90 > 0 && totalOs > 0) {
    const pct = Math.round((total90 / totalOs) * 100);
    out.push({ id: 'overdue', level: pct > 50 ? 'critical' : 'warning',
      icon: <WarningCircle size={13} weight="bold" />,
      title: `${pct}% of outstanding fees are 90+ days overdue`,
      body: `${fmtK(total90)} is in high-risk aging. Recovery probability declines sharply after 90 days.` });
  }

  if (p.collectionRate > 0) {
    if (p.collectionRate >= 80) out.push({ id: 'collect', level: 'positive',
      icon: <CheckCircle size={13} weight="bold" />,
      title: `${Math.round(p.collectionRate)}% collection rate — strong discipline`,
      body: p.isSolo
        ? 'Your collection efficiency is excellent. Consistent follow-up is paying off.'
        : 'Collection efficiency is strong. Consistent follow-up is paying off.' });
    else if (p.collectionRate < 60) out.push({ id: 'collect-low', level: 'warning',
      icon: <Warning size={13} weight="bold" />,
      title: `Collection rate ${Math.round(p.collectionRate)}% — below 80% target`,
      body: p.isSolo
        ? 'Structured payment terms and earlier fee note chasing could improve recovery.'
        : 'Structured payment terms and earlier fee note chasing could improve recovery.' });
  }

  const fortnight = p.upcomingHearings.filter(h =>
    (new Date(h.date).getTime() - Date.now()) / 86_400_000 <= 14);
  if (fortnight.length >= 2) out.push({ id: 'hearing-pressure', level: 'warning',
    icon: <Gavel size={13} weight="bold" />,
    title: `${fortnight.length} hearings in the next 14 days`,
    body: 'High preparation load. Ensure trial bundles and skeleton arguments are on track.' });

  const stalled = p.health.filter(h => h.healthFlag === 'stalled').length;
  if (stalled > 0) out.push({ id: 'stalled', level: 'warning',
    icon: <Clock size={13} weight="bold" />,
    title: `${stalled} matter${stalled > 1 ? 's' : ''} stalled — no activity 21+ days`,
    body: 'Without intervention these matters may miss interim deadlines or slip court windows.' });

  const listed = p.activeMatters.filter(m =>
    m.status === 'listed' || m.status === 'in_trial').length;
  if (listed >= 3) out.push({ id: 'workload', level: 'info',
    icon: <Target size={13} weight="bold" />,
    title: `${listed} matters in active hearing phase`,
    body: `Above-average operational intensity. ${listed} matters are listed or in trial.` });

  return out.slice(0, 4);
}

// ─── Priority actions ──────────────────────────────────────────

interface PriorityAction {
  id: string;
  level: 'critical' | 'warning' | 'info';
  label: string;
}

function buildPriorityActions(p: {
  alerts: Array<{ key: string; level: string; message: string }>;
  health: Array<{ caseId: string; caseNumber: string; daysSinceUpdate: number; healthFlag: string }>;
  outstanding: Array<{ d90: number; clientName: string }>;
}): PriorityAction[] {
  const actions: PriorityAction[] = [];
  for (const a of p.alerts) {
    actions.push({ id: a.key, level: a.level as 'critical' | 'warning' | 'info', label: a.message });
  }
  const stalled = p.health.filter(h => h.healthFlag === 'stalled');
  if (stalled.length === 1) {
    actions.push({ id: `stall-${stalled[0]!.caseId}`, level: 'warning',
      label: `${stalled[0]!.caseNumber} — no activity for ${stalled[0]!.daysSinceUpdate}d` });
  } else if (stalled.length > 1) {
    actions.push({ id: 'stall-many', level: 'warning',
      label: `${stalled.length} matters stalled — 21+ days without activity` });
  }
  const aged = p.outstanding.filter(r => r.d90 > 0);
  if (aged.length > 0) {
    const total = aged.reduce((s, r) => s + r.d90, 0);
    actions.push({ id: 'aged-90d', level: aged.length > 1 ? 'critical' : 'warning',
      label: `${fmtK(total)} aged 90+ days — ${aged.length} client${aged.length > 1 ? 's' : ''}` });
  }
  return actions;
}

// ─── Practice score gauge (SVG semicircle) ────────────────────

function PracticeScoreGauge({ score }: { score: number }) {
  const [animFill, setAnimFill] = useState(0);
  const r = 40; const cx = 50; const cy = 58;
  const circ = Math.PI * r;

  useEffect(() => {
    const t = setTimeout(() => setAnimFill((Math.min(100, Math.max(0, score)) / 100) * circ), 200);
    return () => clearTimeout(t);
  }, [score, circ]);

  const color = score >= 80 ? '#10b981' : score >= 65 ? '#3b82f6' : score >= 45 ? '#f59e0b' : '#ef4444';
  const rating = score >= 80 ? 'STRONG' : score >= 65 ? 'NORMAL' : score >= 45 ? 'ATTENTION' : 'AT RISK';
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 0 ${cx + r} ${cy}`;

  return (
    <svg viewBox="0 0 100 72" className="w-[132px] shrink-0">
      <path d={arc} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" strokeLinecap="round" />
      <path d={arc} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={`${animFill} ${circ + 4}`}
        style={{ transition: 'stroke-dasharray 1.3s cubic-bezier(0.4,0,0.2,1)' }} />
      <text x={cx} y={cy - 17} textAnchor="middle" fill="white" fontSize="26"
        fontWeight="700" fontFamily="IBM Plex Sans,sans-serif" dominantBaseline="middle">
        {score}
      </text>
      <text x={cx} y={cy - 2} textAnchor="middle" fill={color} fontSize="5.5"
        fontWeight="700" fontFamily="IBM Plex Sans,sans-serif" letterSpacing="2">
        {rating}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="rgba(148,163,184,0.4)" fontSize="4.8"
        fontFamily="IBM Plex Sans,sans-serif" letterSpacing="1">
        PRACTICE SCORE
      </text>
    </svg>
  );
}

// ─── Header KPI stat (dark theme) ─────────────────────────────

function HeaderStat({ label, value, sub, ok, spark }: {
  label: string;
  value: string;
  sub?: string;
  ok?: boolean;
  spark?: Array<{ v: number }>;
}) {
  const sparkId = `sp-${label.replace(/\W/g, '')}`;
  return (
    <div className="flex h-full flex-col gap-0.5 min-w-[90px]">
      <div className="text-[21px] font-bold text-white tabular tracking-tight leading-none">{value}</div>
      <div style={{ width: 80, height: 20 }} className="opacity-50 mt-0.5">
      {spark && spark.length > 1 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 1, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={sparkId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="white" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="white" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke="white" strokeWidth={1.5}
                fill={`url(#${sparkId})`} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
      )}
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400 mt-0.5">{label}</div>
      <div className={`min-h-[14px] text-[10px] ${ok === true ? 'text-emerald-400' : ok === false ? 'text-amber-400' : 'text-slate-500'}`}>
        {sub ?? ''}
      </div>
    </div>
  );
}

// ─── Priority action strip ────────────────────────────────────

function PriorityStrip({ actions }: { actions: PriorityAction[] }) {
  if (!actions.length) return null;
  const styles: Record<string, string> = {
    critical: 'border-red-500/30 bg-red-500/10 text-red-300',
    warning:  'border-amber-500/30 bg-amber-500/10 text-amber-300',
    info:     'border-blue-500/30 bg-blue-500/10 text-blue-300',
  };
  const dots: Record<string, string> = {
    critical: 'bg-red-400', warning: 'bg-amber-400', info: 'bg-blue-400',
  };
  return (
    <div className="flex gap-2 overflow-x-auto overflow-y-hidden pb-0.5" style={{ scrollbarWidth: 'none' }}>
      {actions.map(a => (
        <div key={a.id}
          className={`shrink-0 flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${styles[a.level] ?? styles.info}`}>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dots[a.level] ?? 'bg-blue-400'}`} />
          {a.label}
        </div>
      ))}
    </div>
  );
}

// ─── Custom tooltips ──────────────────────────────────────────

function RevTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const billed = payload.find((p: any) => p.dataKey === 'billed')?.value ?? 0;
  const collected = payload.find((p: any) => p.dataKey === 'collected')?.value ?? 0;
  const eff = billed > 0 ? Math.round((collected / billed) * 100) : 0;
  return (
    <div className="min-w-[176px] rounded-xl border border-border bg-card/95 p-3.5 shadow-pop backdrop-blur-sm">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="space-y-1.5">
        {[{ label: 'Billed', val: billed, color: '#2563EB' }, { label: 'Collected', val: collected, color: '#059669' }].map(r => (
          <div key={r.label} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />{r.label}
            </span>
            <span className="font-bold tabular text-foreground">{fmt(r.val)}</span>
          </div>
        ))}
        <div className="mt-1.5 border-t border-border pt-1.5 text-[10px]">
          <span className="text-muted-foreground">Efficiency: </span>
          <span className={`font-bold ${eff >= 80 ? 'text-emerald-600 dark:text-emerald-400' : eff >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
            {eff}%
          </span>
        </div>
      </div>
    </div>
  );
}

function HearingTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s: number, p: any) => s + (p.value ?? 0), 0);
  return (
    <div className="min-w-[150px] rounded-xl border border-border bg-card/95 p-3.5 shadow-pop backdrop-blur-sm">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="space-y-1">
        {payload.map((p: any) => (
          <div key={p.dataKey} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: p.fill }} />{p.name}
            </span>
            <span className="font-semibold text-foreground">{p.value}</span>
          </div>
        ))}
        <div className="mt-1 border-t border-border pt-1 text-[10px]">
          <span className="text-muted-foreground">Total: </span>
          <span className="font-bold text-foreground">{total}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Collection funnel ────────────────────────────────────────

function CollectionFunnel({ billed, collected, outstanding, aged90 }: {
  billed: number; collected: number; outstanding: number; aged90: number;
}) {
  const rows = [
    { label: 'Total Billed',   val: billed,      pct: 100, color: '#2563EB', bg: 'bg-blue-500' },
    { label: 'Collected',      val: collected,   pct: billed > 0 ? Math.round((collected / billed) * 100) : 0,    color: '#059669', bg: 'bg-emerald-500' },
    { label: 'Outstanding',    val: outstanding, pct: billed > 0 ? Math.round((outstanding / billed) * 100) : 0,  color: '#D97706', bg: 'bg-amber-500' },
    { label: '90d+ At Risk',   val: aged90,      pct: billed > 0 ? Math.round((aged90 / billed) * 100) : 0,       color: '#EF4444', bg: 'bg-red-500' },
  ];
  return (
    <div className="space-y-4 py-1">
      {rows.map(r => (
        <div key={r.label}>
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{r.label}</span>
            <span className="font-bold tabular text-foreground">{fmtK(r.val)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted/40">
            <div className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${r.pct}%`, background: r.color }} />
          </div>
          <div className="mt-0.5 text-right text-[9px] text-muted-foreground">{r.pct}% of billed</div>
        </div>
      ))}
    </div>
  );
}

// ─── Chart shell ──────────────────────────────────────────────

/** Chart height: three quarters of the desktop figure below sm. Same data, narrower box. */
function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => (typeof window === 'undefined' ? false : window.innerWidth < 640));
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

function useChartH(h: number) {
  return useIsNarrow() ? Math.round(h * 0.75) : h;
}

function ChartShell({ h, loading, empty, emptyMsg, children }: {
  h: number; loading?: boolean; empty?: boolean; emptyMsg?: string; children?: React.ReactNode;
}) {
  // Phones get a shorter chart: the page carries seven of these and ran to five screens.
  h = useChartH(h);
  if (loading) return (
    <div className="flex items-center justify-center rounded-xl bg-muted/20" style={{ height: h }}>
      <CircleNotch size={18} className="animate-spin text-muted-foreground/30" />
    </div>
  );
  if (empty) return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border" style={{ height: h }}>
      <ChartBar size={20} className="text-muted-foreground/20" weight="duotone" />
      <span className="text-[10px] text-muted-foreground/50">{emptyMsg ?? 'No data'}</span>
    </div>
  );
  return <>{children}</>;
}

// ─── Section label ────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="h-px flex-1 bg-border/50" />
      <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/50">{children}</span>
      <span className="h-px flex-1 bg-border/50" />
    </div>
  );
}

// ─── Insight card ─────────────────────────────────────────────

function InsightCard({ level, icon, title, body }: {
  level: string; icon: React.ReactNode; title: string; body: string;
}) {
  const s = {
    critical: 'border-red-200/80 bg-red-50/60 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
    warning:  'border-amber-200/80 bg-amber-50/50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
    positive: 'border-emerald-200/80 bg-emerald-50/50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
    info:     'border-blue-200/80 bg-blue-50/40 text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300',
  }[level] ?? 'border-border bg-muted/30 text-foreground';
  return (
    <div className={`rounded-xl border p-4 ${s}`}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="opacity-80">{icon}</span>
        <span className="text-xs font-semibold leading-snug">{title}</span>
      </div>
      <p className="text-xs leading-relaxed opacity-80">{body}</p>
    </div>
  );
}

// ─── Palette ──────────────────────────────────────────────────

const PALETTE = ['#2563EB','#059669','#D97706','#7C3AED','#0D9488','#DB2777','#64748B'];

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function MyReportsPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const { isSolo } = useChambersMode();
  const allMatters  = useMattersStore(s => s.matters);
  const fetchMatters = useMattersStore(s => s.fetchMatters);
  const allHearings  = useHearingsStore(s => s.hearings);
  const fetchHearings = useHearingsStore(s => s.fetchHearings);
  const chartHeight = useChartH(210);
  const [range, setRange] = useState<RangeMode>('year');

  useEffect(() => { fetchMatters(); fetchHearings(); }, [fetchMatters, fetchHearings]);

  const { from, to } = useMemo(() => dateRange(range), [range]);

  // ── API queries ──────────────────────────────────────────────
  const finQ  = useQuery({ queryKey: ['rpt-fin', from, to], queryFn: () => fetchFinancialPerformance(from, to), staleTime: 0 });
  const outQ  = useQuery({ queryKey: ['rpt-out'],  queryFn: fetchOutstandingFees,   staleTime: 0 });
  const alrtQ = useQuery({ queryKey: ['rpt-alrt'], queryFn: fetchOperationalAlerts, staleTime: 0, refetchInterval: 60_000 });
  const hlthQ = useQuery({ queryKey: ['rpt-hlth'], queryFn: () => fetchMatterHealth(21), staleTime: 0 });
  const hwQ   = useQuery({ queryKey: ['rpt-hw'],   queryFn: () => fetchHearingWorkloadTrend(12), staleTime: 0 });
  const metricsQ = useQuery({ queryKey: ['dashboard-metrics'], queryFn: fetchDashboardMetrics });

  // ── Refusal is not a load failure ────────────────────────────────────────────────────────
  // REPORT_ROLES (reports.routes.ts) does not serve pupils or door tenants, so for them every
  // report query answers 403. That was rendered as "figures below may be incomplete — please
  // retry" over a page of empty charts: a permission decision dressed up as a transient fault,
  // with a Retry that could never succeed. Say what it is instead. This reports the server's
  // answer; it does not decide access.
  const statusOf = (q: { error: unknown }) =>
    (q.error as { response?: { status?: number } } | null)?.response?.status;
  const reportQueries = [finQ, outQ, alrtQ, hlthQ, hwQ, metricsQ];
  const isRefused = reportQueries.some((q) => statusOf(q) === 403);
  const hasLoadFailure = reportQueries.some((q) => q.isError && statusOf(q) !== 403);

  // ── Derived ──────────────────────────────────────────────────
  const myMatters = useMemo(() => {
    if (!user?.id) return allMatters;
    return allMatters.filter(m => m.leadBarristerUserId === user.id || m.juniorBarristerUserId === user.id);
  }, [allMatters, user]);

  const activeMatters  = myMatters.filter(m => isOpenMatterStatus(m.status));
  // Win rate: backend-owned fact (canonical computeWinRate; null below a 3-matter sample). UI renders only.
  const winRate = metricsQ.data?.practiceHealth.winRate ?? null;

  const upcomingHearings = allHearings
    .filter(h => new Date(h.date) > new Date() && ['scheduled','confirmed'].includes(h.status))
    .sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const nextHearing     = upcomingHearings[0];
  const nextHearingDays = nextHearing
    ? Math.ceil((new Date(nextHearing.date).getTime() - Date.now()) / 86_400_000) : null;

  const fin         = finQ.data?.summary;
  const monthly     = (finQ.data?.monthly ?? []).map(d => ({ ...d, month: monthLabel(d.month) }));
  const byArea      = finQ.data?.byPracticeArea ?? [];
  const outstanding = outQ.data ?? [];
  const health      = hlthQ.data ?? [];
  const alerts      = alrtQ.data ?? [];

  // All-time current debt — drives the Payment Risk Monitor + funnel (labelled
  // "all matters"), NOT the ranged KPI cards (those use fin.* so they reconcile).
  const totalOutstanding = outstanding.reduce((s, r) => s + r.total, 0);
  const totalOverdue90   = outstanding.reduce((s, r) => s + r.d90, 0);
  const stalledCount     = health.filter(h => h.healthFlag === 'stalled').length;
  const atRiskCount      = health.filter(h => h.healthFlag === 'at_risk').length;

  // Hearing monthly buckets — authoritative, access-scoped backend trend
  // (/reports/hearing-workload-trend). Previously bucketed client-side from the
  // hearings store, which depended on what was loaded and bypassed scoping.
  const hearingsByMonth = useMemo(
    () => (hwQ.data ?? []).map(m => ({ month: monthLabel(m.month), completed: m.completed, scheduled: m.scheduled, adjourned: m.adjourned })),
    [hwQ.data],
  );

  // Sparkline data (monthly billed/collected)
  const billedSpark     = useMemo(() => (finQ.data?.monthly ?? []).map(d => ({ v: d.billed })), [finQ.data]);
  const collectedSpark  = useMemo(() => (finQ.data?.monthly ?? []).map(d => ({ v: d.collected })), [finQ.data]);
  const rateSpark       = useMemo(() => (finQ.data?.monthly ?? []).map(d => ({
    v: d.billed > 0 ? Math.round((d.collected / d.billed) * 100) : 0,
  })), [finQ.data]);

  // Practice score
  const practiceScore = useMemo(() => fin
    ? computePracticeScore({
        collectionRate: fin.collectionRate,
        stalledCount,
        atRiskCount,
        activeCount: activeMatters.length,
        criticalAlerts: alerts.filter(a => a.level === 'critical').length,
      })
    : 0,
  [fin, stalledCount, atRiskCount, activeMatters.length, alerts]);

  // Insights + priority actions
  const insights = useMemo(() => deriveInsights({
    monthly: finQ.data?.monthly ?? [],
    outstanding,
    health,
    upcomingHearings,
    activeMatters,
    collectionRate: fin?.collectionRate ?? 0,
    totalBilled: fin?.totalBilled ?? 0,
    isSolo,
  }), [finQ.data, outstanding, health, upcomingHearings, activeMatters, fin, isSolo]);

  const priorityActions = useMemo(() => buildPriorityActions({ alerts, health, outstanding }),
    [alerts, health, outstanding]);

  const RANGES: { key: RangeMode; label: string }[] = [
    { key: 'month', label: '1M' }, { key: 'quarter', label: '3M' },
    { key: 'half', label: '6M' }, { key: 'year', label: '1Y' },
  ];
  // Honest caption for range-driven charts — reflects the actual selection, not a fixed "12 months".
  const RANGE_CAPTION: Record<RangeMode, string> = {
    month: 'Last month', quarter: 'Last 3 months', half: 'Last 6 months', year: 'Last 12 months',
  };

  // The server has refused this role outright, so every figure below would be empty and every
  // retry would fail. One plain statement beats a page of zeros under a "please retry" banner.
  if (isRefused) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <WarningCircle size={22} weight="fill" className="text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">Practice reports aren't available to your role</p>
          <p className="mt-1 max-w-sm text-xs text-muted-foreground">
            Chambers reporting is restricted to tenants and chambers staff. Nothing has gone wrong,
            and there is nothing to retry.
          </p>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen pb-16">
      {/* Every page needs one top-level heading: it is what a screen reader announces on
          arrival and what heading navigation jumps between. This screen's design has no
          single visible title, so the heading is for assistive technology only and the
          layout is untouched. */}
      <h1 className="sr-only">Reports</h1>

      <BackButton onClick={() => navigate(-1)} />

      {/* ── COMMAND HEADER ───────────────────────────────────── */}
      <div className="mb-8 overflow-hidden rounded-2xl bg-[#0a0f1e]">
        <div className="relative px-6 py-5">

          {/* Background glows */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-blue-600/8 blur-3xl" />
            <div className="absolute bottom-0 left-1/4 h-48 w-48 rounded-full bg-violet-600/6 blur-3xl" />
          </div>

          {/* Top row: label + controls */}
          <div className="relative mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                {isSolo ? 'Your Practice Overview' : 'Legal Operations Intelligence'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5 rounded-xl border border-white/10 bg-white/5 p-1">
                {RANGES.map(r => (
                  <button key={r.key} onClick={() => setRange(r.key)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-150 ${
                      range === r.key ? 'bg-white text-[#0a0f1e] shadow-sm' : 'text-slate-400 hover:text-white'
                    }`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => fin && downloadCSV(`practice-${to}.csv`, monthly)}
                className="flex h-8 items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 text-xs font-medium text-slate-400 transition hover:bg-white/10 hover:text-white">
                <DownloadSimple size={12} weight="bold" /> Export
              </button>
            </div>
          </div>

          {/* Main header grid: Score | KPIs | Context */}
          <div className="relative flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-start sm:gap-6 lg:flex-nowrap">

            {/* Practice score gauge */}
            <div className="flex shrink-0 flex-col items-center gap-1 self-center sm:self-start">
              <PracticeScoreGauge score={practiceScore} />
              <div className="mt-1 text-center">
                <div className="text-sm font-bold text-white">
                  {isSolo ? 'Solo Practice' : 'Practice Command'}
                </div>
                <div className="text-[10px] text-slate-500">
                  {myMatters.length} matters · {allHearings.length} hearings
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden h-auto w-px self-stretch bg-white/8 lg:block" />

            {/* KPI sparkline stats */}
            <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-4 sm:flex sm:flex-wrap sm:gap-6">
              <HeaderStat
                label="Total Billed"
                value={fin ? fmtK(fin.totalBilled) : '—'}
                sub={`${fin?.feeNoteCount ?? 0} fee notes`}
                spark={billedSpark}
              />
              <HeaderStat
                label="Collected"
                value={fin ? fmtK(fin.totalCollected) : '—'}
                sub={fin ? `${Math.round(fin.collectionRate)}% rate` : undefined}
                ok={fin ? fin.collectionRate >= 80 : undefined}
                spark={collectedSpark}
              />
              {/* Ranged, same fee-note set as Billed/Collected so the four KPIs reconcile
                  (Billed = Collected + Outstanding + credits/write-offs). All-time aged
                  debt lives in the Payment Risk Monitor below, which is labelled as such. */}
              <HeaderStat
                label="Outstanding"
                value={fin ? fmtK(fin.totalOutstanding) : '—'}
                sub="of fees billed in period"
                ok={fin ? fin.totalOutstanding === 0 : undefined}
              />
              <HeaderStat
                label="Collection Rate"
                value={fin ? `${Math.round(fin.collectionRate)}%` : '—'}
                sub={fin && fin.collectionRate >= 80 ? 'above 80% target' : 'below target'}
                ok={fin ? fin.collectionRate >= 80 : undefined}
                spark={rateSpark}
              />
            </div>

            {/* Divider */}
            <div className="hidden h-auto w-px self-stretch bg-white/8 lg:block" />

            {/* Context stats */}
            <div className="flex shrink-0 flex-row flex-wrap justify-between gap-3 sm:flex-col sm:justify-start">
              {[
                { label: 'Active Matters', val: activeMatters.length, icon: <Scales size={12} /> },
                { label: 'Upcoming Hearings', val: upcomingHearings.length, icon: <Gavel size={12} /> },
                { label: 'Win Rate', val: winRate === null ? '—' : `${winRate}%`, icon: <Target size={12} /> },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2.5">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/8 text-slate-400">
                    {s.icon}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{s.val}</div>
                    <div className="text-[9px] text-slate-500">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Critical alerts */}
          {alerts.filter(a => a.level === 'critical').map(a => (
            <div key={a.key}
              className="relative mt-4 flex items-center gap-2.5 rounded-lg border border-red-500/20 bg-red-500/8 px-4 py-2.5">
              <WarningCircle size={13} weight="fill" className="shrink-0 text-red-400" />
              <span className="text-xs font-medium text-red-300">{a.message}</span>
              <span className="ml-auto rounded-full border border-red-500/30 bg-red-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-400">
                Critical
              </span>
            </div>
          ))}

          {/* Priority action strip */}
          {priorityActions.length > 0 && (
            <div className="relative mt-4 border-t border-white/6 pt-4">
              <PriorityStrip actions={priorityActions} />
            </div>
          )}
        </div>
      </div>

      {/* Load-failure banner — surface a real error instead of silently rendering
          '—' / £0, which reads as "no data" when the fetch actually failed.
          A 403 is excluded: that is a refusal, handled above, and offering Retry for it lies. */}
      {hasLoadFailure && (
        <div className="mb-6 flex items-center gap-2.5 rounded-lg border border-red-500/30 bg-red-500/8 px-4 py-3">
          <WarningCircle size={16} weight="fill" className="shrink-0 text-red-500" />
          <span className="text-sm font-medium text-red-600 dark:text-red-400">
            Some report data couldn't be loaded — the figures below may be incomplete. Please retry.
          </span>
          <button
            onClick={() => { finQ.refetch(); outQ.refetch(); hlthQ.refetch(); hwQ.refetch(); alrtQ.refetch(); metricsQ.refetch(); }}
            className="ml-auto rounded-md border border-red-500/30 px-3 py-1 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/10"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── OPERATIONAL INTELLIGENCE ─────────────────────────── */}
      {insights.length > 0 && (
        <>
          <SectionLabel>Operational Intelligence</SectionLabel>
          <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {insights.map(ins => (
              <InsightCard key={ins.id} level={ins.level} icon={ins.icon} title={ins.title} body={ins.body} />
            ))}
          </div>
        </>
      )}

      {/* ── REVENUE INTELLIGENCE ─────────────────────────────── */}
      <SectionLabel>Revenue Intelligence</SectionLabel>
      {/* Same `[&>*]:min-w-0` as the Operational Activity grid below, and for the same reason:
          an `auto` column is sized by its item's minimum CONTENT width, not by the screen. */}
      <div className="mb-6 grid gap-5 [&>*]:min-w-0 md:grid-cols-5">

        {/* Revenue area chart */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5 md:col-span-3">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <div className="text-sm font-semibold text-foreground">Billing &amp; Collection Trend</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{RANGE_CAPTION[range]} · billed vs collected</div>
            </div>
            {fin && (
              <div className="text-right">
                <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Efficiency</div>
                <div className={`text-[20px] font-bold tabular ${fin.collectionRate >= 80 ? 'text-emerald-600 dark:text-emerald-400' : fin.collectionRate >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                  {Math.round(fin.collectionRate)}%
                </div>
              </div>
            )}
          </div>
          <ChartShell h={210} loading={finQ.isLoading} empty={monthly.length === 0} emptyMsg="No billing data in period">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <AreaChart data={monthly} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="gBilled" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gColl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `£${v/1000}k`} tick={{ fontSize: 9, fill: '#94a3b8' }}
                  axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<RevTooltip />} />
                <Area type="monotone" dataKey="billed" name="Billed" stroke="#2563EB" strokeWidth={2.5}
                  fill="url(#gBilled)" dot={false}
                  activeDot={{ r: 5, fill: '#2563EB', strokeWidth: 2, stroke: '#fff' }} />
                <Area type="monotone" dataKey="collected" name="Collected" stroke="#059669" strokeWidth={2}
                  fill="url(#gColl)" dot={false}
                  activeDot={{ r: 4, fill: '#059669', strokeWidth: 2, stroke: '#fff' }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartShell>
          {/* Legend */}
          <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground">
            {[{ c:'#2563EB', l:'Billed' },{ c:'#059669', l:'Collected' }].map(x => (
              <span key={x.l} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: x.c }} />{x.l}
              </span>
            ))}
          </div>
        </div>

        {/* Collection funnel */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5 md:col-span-2">
          <div className="mb-1 text-sm font-semibold text-foreground">Revenue Pipeline</div>
          <div className="mb-4 text-xs text-muted-foreground">From billing to collection — leakage at each stage</div>
          {finQ.isLoading ? <ChartShell h={180} loading /> : (
            <CollectionFunnel
              billed={fin?.totalBilled ?? 0}
              collected={fin?.totalCollected ?? 0}
              outstanding={fin?.totalOutstanding ?? 0}
              aged90={totalOverdue90}
            />
          )}
          {fin && (
            <div className="mt-4 rounded-xl border border-dashed border-border bg-muted/20 px-4 py-3 text-center">
              <div className="text-[10px] text-muted-foreground">Average fee per matter</div>
              <div className="mt-0.5 text-[16px] font-bold tabular text-foreground">
                {myMatters.length > 0 ? fmtK(Math.round(fin.totalBilled / myMatters.length)) : '—'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── OPERATIONAL ACTIVITY ─────────────────────────────── */}
      <SectionLabel>Operational Activity</SectionLabel>
      {/* `[&>*]:min-w-0`: a grid item's `min-width` is `auto`, so the single column below `md`
          was sized by the Hearing Workload card's minimum content — its title and the three-dot
          legend side by side — rather than by the screen, and both cards hung 7px over the edge
          of a 320px phone. */}
      <div className="mb-6 grid gap-5 [&>*]:min-w-0 md:grid-cols-5">

        {/* Hearing workload */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5 md:col-span-3">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">Hearing Workload</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Last 12 months · by status · not affected by the range selector</div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
              {[{ c:'#059669',l:'Completed' },{ c:'#2563EB',l:'Scheduled' },{ c:'#D97706',l:'Adjourned' }].map(x => (
                <span key={x.l} className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: x.c }} />{x.l}</span>
              ))}
            </div>
          </div>
          <ChartShell h={210} loading={hwQ.isLoading} empty={hearingsByMonth.every(m => m.completed + m.scheduled + m.adjourned === 0)} emptyMsg="No hearing data">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={hearingsByMonth} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barSize={9} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={1} />
                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<HearingTooltip />} />
                <Bar dataKey="completed" name="Completed" stackId="a" fill="#059669" />
                <Bar dataKey="scheduled" name="Scheduled" stackId="a" fill="#2563EB" />
                <Bar dataKey="adjourned" name="Adjourned" stackId="a" fill="#D97706" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartShell>
        </div>

        {/* Payment risk */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5 md:col-span-2">
          <div className="mb-1 text-sm font-semibold text-foreground">Payment Risk Monitor</div>
          <div className="mb-4 text-xs text-muted-foreground">Aged receivables · client exposure · current, all matters (not filtered by range)</div>
          {outQ.isLoading ? <ChartShell h={210} loading /> :
           outstanding.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <CheckCircle size={30} weight="duotone" className="text-emerald-500" />
              <div className="text-sm font-semibold text-foreground">All fees collected</div>
              <div className="text-xs text-muted-foreground">No outstanding balances</div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Aging buckets */}
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { l: 'Current', v: outstanding.reduce((s,r) => s+r.current, 0), color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100' },
                  { l: '1–30d',   v: outstanding.reduce((s,r) => s+r.d30, 0),    color: 'text-amber-600 dark:text-amber-400',  bg: 'bg-amber-50 dark:bg-amber-500/10 border-amber-100' },
                  { l: '31–60d',  v: outstanding.reduce((s,r) => s+r.d60, 0),    color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-500/10 border-orange-100' },
                  { l: '90d+',    v: outstanding.reduce((s,r) => s+r.d90, 0),    color: 'text-red-600 dark:text-red-400',    bg: 'bg-red-50 dark:bg-red-500/10 border-red-100' },
                ].map(b => (
                  <div key={b.l} className={`rounded-xl border p-2 text-center ${b.bg}`}>
                    <div className={`text-xs font-bold tabular ${b.color}`}>{fmtK(b.v)}</div>
                    <div className="mt-0.5 text-[8.5px] text-muted-foreground">{b.l}</div>
                  </div>
                ))}
              </div>
              {/* Per-client rows */}
              <div className="space-y-1.5">
                {outstanding.slice(0, 4).map(r => {
                  const pct90 = r.total > 0 ? Math.round((r.d90 / r.total) * 100) : 0;
                  return (
                    <div key={r.contactId ?? r.clientName}
                      className="overflow-hidden rounded-xl border border-border/50 px-3 py-2.5">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <div className="truncate text-xs font-semibold text-foreground">{r.clientName}</div>
                          <div className="mt-0.5 text-[9px] text-muted-foreground">{r.mattersCount} matter{r.mattersCount !== 1 ? 's' : ''}</div>
                        </div>
                        <div className="ml-2 shrink-0 text-right">
                          <div className="text-xs font-semibold text-foreground">{fmt(r.total)}</div>
                          {r.d90 > 0 && <div className="mt-0.5 text-[9px] font-bold text-red-600 dark:text-red-400">⚠ 90d+ risk</div>}
                        </div>
                      </div>
                      {r.d90 > 0 && (
                        <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted/40">
                          <div className="h-full rounded-full bg-red-500 transition-all duration-700"
                            style={{ width: `${pct90}%` }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="rounded-xl border border-dashed border-border bg-muted/10 px-3 py-2.5 text-center">
                <span className="text-xs font-bold text-foreground">Total outstanding: {fmt(totalOutstanding)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── PRACTICE ANALYTICS ───────────────────────────────── */}
      <SectionLabel>Practice Analytics</SectionLabel>
      <div className="mb-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

        {/* Revenue by area */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5">
          <div className="mb-4 text-sm font-semibold text-foreground">Revenue by Practice Area</div>
          {finQ.isLoading ? <ChartShell h={180} loading /> :
           byArea.length === 0 ? <ChartShell h={180} empty emptyMsg="No billing data" /> : (
            <div className="space-y-3.5">
              {byArea.slice(0, 6).map((a, i) => {
                const pct = fin?.totalBilled ? Math.round((a.billed / fin.totalBilled) * 100) : 0;
                const collEff = a.billed > 0 ? Math.round((a.collected / a.billed) * 100) : 0;
                return (
                  <div key={a.practiceArea ?? i}>
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 font-medium text-foreground">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                        {a.practiceArea || 'Unclassified'}
                      </span>
                      <span className="tabular text-muted-foreground">
                        {fmtK(a.billed)}
                        <span className="ml-1.5 font-semibold text-foreground">{pct}%</span>
                      </span>
                    </div>
                    <div className="relative h-1.5 overflow-hidden rounded-full bg-muted/40">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length] }} />
                    </div>
                    <div className="mt-0.5 text-right text-[9px] text-muted-foreground">{collEff}% collected</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Matter health */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5">
          <div className="mb-1 text-sm font-semibold text-foreground">Matter Health Monitor</div>
          <div className="mb-4 text-xs text-muted-foreground">Matters requiring attention · current state (not filtered by range)</div>
          {hlthQ.isLoading ? <ChartShell h={180} loading /> :
           health.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <CheckCircle size={30} weight="duotone" className="text-emerald-500" />
              <div className="text-sm font-semibold text-foreground">All matters progressing</div>
              <div className="text-xs text-muted-foreground">No stalled or at-risk matters</div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="mb-3 flex gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${stalledCount > 0 ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300' : 'bg-muted text-muted-foreground'}`}>
                  {stalledCount} stalled
                </span>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${atRiskCount > 0 ? 'bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300' : 'bg-muted text-muted-foreground'}`}>
                  {atRiskCount} at risk
                </span>
              </div>
              {health.slice(0, 5).map(m => (
                <button key={m.caseId} onClick={() => navigate(`/barrister/matters/${m.caseId}`)}
                  className="flex w-full items-center justify-between rounded-xl border border-border/50 px-3 py-2 text-left transition hover:border-brand-200 hover:bg-brand-50/20 hover:shadow-sm">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-medium text-foreground">{m.caseNumber}</div>
                    <div className="text-[9px] text-muted-foreground">{m.daysSinceUpdate}d without activity</div>
                  </div>
                  <div className="ml-2 flex items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${m.healthFlag === 'stalled' ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'}`}>
                      {m.healthFlag.replace('_',' ')}
                    </span>
                    <ArrowUpRight size={10} className="text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Hearing pressure */}
        <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-card sm:p-5">
          <div className="mb-1 text-sm font-semibold text-foreground">Hearing Pressure</div>
          <div className="mb-4 text-xs text-muted-foreground">Upcoming court appearances</div>
          {upcomingHearings.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <Calendar size={30} weight="duotone" className="text-muted-foreground/30" />
              <div className="text-xs text-muted-foreground">No upcoming hearings</div>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingHearings.slice(0, 5).map(h => {
                const days = Math.ceil((new Date(h.date).getTime() - Date.now()) / 86_400_000);
                const urgent = days <= 7; const soon = days <= 14;
                return (
                  <div key={h.id} className={`relative overflow-hidden rounded-xl border px-3 py-2.5 ${urgent ? 'border-red-200 dark:border-red-500/30 bg-red-50/40 dark:bg-red-500/10' : soon ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50/30 dark:bg-amber-500/10' : 'border-border/50 bg-card'}`}>
                    <div className="flex items-start gap-2.5">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${urgent ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' : soon ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'bg-brand-50 text-brand-700 dark:text-brand-300'}`}>
                        {days}d
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-foreground">{h.type}</div>
                        <div className="text-[9px] text-muted-foreground">
                          {h.matterNumber} · {new Date(h.date).toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' })}
                        </div>
                        {h.court && <div className="text-[9px] text-muted-foreground">{h.court}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {nextHearingDays !== null && (
                <div className="mt-1 rounded-xl border border-dashed border-border bg-muted/10 px-3 py-2 text-center text-[10px] text-muted-foreground">
                  Next hearing in <span className="font-bold text-foreground">{nextHearingDays} day{nextHearingDays !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── ACTIVE MATTER TABLE ───────────────────────────────── */}
      <SectionLabel>Active Matter Intelligence</SectionLabel>
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-card">
        {/* Table header */}
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4 border-b border-border/50 bg-muted/30 px-5 py-2.5 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">
          <span>Matter</span>
          <span>Status</span>
          <span>Fee Estimate</span>
          <span>Next Hearing</span>
          <span>Health</span>
        </div>
        {/* Rows */}
        {activeMatters.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <Scales size={28} weight="duotone" className="text-muted-foreground/20" />
            <div className="text-xs text-muted-foreground">No active matters</div>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {activeMatters.slice(0, 8).map(m => {
              const hFlag = health.find(h => h.caseId === m.id)?.healthFlag;
              const upcoming = allHearings
                .filter(h => h.matterId === m.id && new Date(h.date) > new Date() && ['scheduled','confirmed'].includes(h.status))
                .sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
              const nextH = upcoming[0];
              const daysToH = nextH ? Math.ceil((new Date(nextH.date).getTime() - Date.now()) / 86_400_000) : null;

              const statusDot: Record<string, string> = {
                in_trial: 'bg-amber-500 animate-pulse',
                listed:   'bg-blue-500',
                on_hold:  'bg-slate-400',
                active:   'bg-emerald-500',
              };

              return (
                <button key={m.id} onClick={() => navigate(`/barrister/matters/${m.id}`)}
                  className="group grid w-full grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-3.5 text-left transition-colors hover:bg-muted/20">
                  {/* Matter name */}
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${statusDot[m.status] ?? 'bg-muted-foreground/40'}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-xs font-semibold text-foreground">{m.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[9.5px] text-muted-foreground">
                        <span className="">{m.number}</span>
                        {m.area && <><span>·</span><span>{m.area}</span></>}
                      </div>
                    </div>
                  </div>
                  {/* Status */}
                  <div className="flex items-center">
                    <span className={`rounded-md px-2 py-0.5 text-[9px] font-bold capitalize ${
                      m.status === 'listed' ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300' :
                      m.status === 'in_trial' ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' :
                      m.status === 'on_hold' ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' :
                      'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    }`}>
                      {m.status.replace(/_/g,' ')}
                    </span>
                  </div>
                  {/* Fee estimate */}
                  <div className="flex items-center">
                    {m.feeEstimate > 0 ? (
                      <span className="tabular text-xs font-semibold text-foreground">{fmtK(m.feeEstimate)}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </div>
                  {/* Next hearing */}
                  <div className="flex items-center">
                    {daysToH !== null ? (
                      <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                        daysToH <= 7 ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300' :
                        daysToH <= 14 ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300' :
                        'bg-brand-50 text-brand-700 dark:text-brand-300'
                      }`}>
                        {nextH?.type} · {daysToH}d
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/50">—</span>
                    )}
                  </div>
                  {/* Health */}
                  <div className="flex items-center justify-between">
                    {hFlag ? (
                      <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${
                        hFlag === 'stalled' ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'
                      }`}>
                        {hFlag.replace('_',' ')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[9px] text-emerald-600 dark:text-emerald-400">
                        <CheckCircle size={10} weight="fill" /> on track
                      </span>
                    )}
                    <ArrowRight size={11} className="text-muted-foreground opacity-0 transition group-hover:opacity-60" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {activeMatters.length > 8 && (
          <div className="border-t border-border/40 px-5 py-3 text-center">
            <button onClick={() => navigate('/barrister/matters')}
              className="flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground transition hover:text-brand-600 dark:hover:text-brand-400">
              View all {activeMatters.length} active matters <ArrowRight size={11} />
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
